import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import Vimeo from 'react-player/vimeo';
import cn from 'classnames';

import {PageType} from 'coreUtils/IUtils';

import {useCarouselControllerContext} from 'brandComponents/Carousel';
import useCurrentPageType from 'brandHooks/useCurrentPageType';

import {IVimeoVideoProps} from './IVimeoVideoProps';
import {MuteButton, PlayButton} from './Subcomponents';

import styles from './VimeoVideo.module.scss';

const VimeoVideo = ({vimeoVideo, videoConfig, controlsConfig}: IVimeoVideoProps) => {
	const carouselContext = useCarouselControllerContext(true);
	const isInCarousel = carouselContext?.isInCarousel;
	const carouselHasLoop = carouselContext?.swiperRef?.current?.params.loop;
	const pageType = useCurrentPageType();

	const {showControls, autoplay, muted, loop} = videoConfig;
	const isAutoplay = autoplay && muted;

	const [isPlaying, setIsPlaying] = useState<boolean>(isAutoplay);
	const [isMuted, setIsMuted] = useState<boolean>(muted);
	const [isVisible, setIsVisible] = useState<boolean>(false);
	const [initializeVideo, setInitializeVideo] = useState<boolean>(false);

	const videoContainer = useRef();
	const overlayRef = useRef(null);
	const vimeoWrapperRef = useRef(null);

	const src =  useMemo(() => vimeoVideo.getVideoUrl(), [vimeoVideo]);
	const thumbnailUrl = useMemo(() => vimeoVideo.getThumbnailUrl(), [vimeoVideo]);
	const aspectRatio = useMemo(() => vimeoVideo.getAspectRatio() || '16/9', [vimeoVideo]);

	const toggleThumbnail = useCallback((show) =>
		show
			? overlayRef.current.classList.remove('d-none')
			: overlayRef.current.classList.add('d-none')
	, []);

	const APIOptions = {
		playerOptions: {
			controls: !controlsConfig && showControls,
			responsive: true,
			fullscreen: false
		}
	};

	useEffect(() => {
		if (pageType !== PageType.PDP) {
			setIsPlaying(isVisible && autoplay);
		}
	}, [isVisible]);

	useEffect(() => {
		/*
			We render the player only when the wrapper is visible because if it's in display none (so without dimensions),
			the player sets the quality to the lower one
		*/
		const options = {
			threshold: 0.1
		};

		const observer = new IntersectionObserver(([entry]) => {
			if (entry.isIntersecting) {
				setIsVisible(true);
				setInitializeVideo(true);
			} else {
				setIsVisible(false);

				if (isInCarousel && carouselHasLoop && pageType !== PageType.PDP) {
					setInitializeVideo(false);
					toggleThumbnail(true);
				}
			}
		}, options);

		if (vimeoWrapperRef.current) {
			observer.observe(vimeoWrapperRef.current);
		}

		return () => {
			if (vimeoWrapperRef.current) {
				observer.unobserve(vimeoWrapperRef.current);
			}
		};
	}, [isInCarousel, carouselHasLoop]);

	return (
		<div className={cn('vimeo-video-wrapper', styles['vimeo-video-wrapper'])} key={src} ref={vimeoWrapperRef}>
			<div className={cn('vimeo-video-thumbnail', styles['vimeo-video-thumbnail'])} ref={overlayRef}>
				<img src={thumbnailUrl} />
			</div>
			<div
				className="video-embed"
				style={{aspectRatio}}
				ref={videoContainer}
			>
				{initializeVideo &&
					<Vimeo
						url={src}
						playing={isPlaying}
						loop={loop}
						muted={isMuted}
						volume={1}
						onReady={!isAutoplay ? (() => toggleThumbnail(false)) : undefined}
						onStart={isAutoplay ? (() => toggleThumbnail(false)) : undefined}
						onError={(e)=> console.error(e)}
						config={APIOptions}
						height="auto"
						width="auto"
						playsinline
					/>
				}
			</div>
			{(showControls && controlsConfig) && (
				<div className={cn('vimeo-video-controls', styles['vimeo-video-controls'])}>
					{controlsConfig?.playButton && (
						<PlayButton
							setIsPlaying={setIsPlaying}
							isPlaying={isPlaying}
							icons={{
								play: controlsConfig.playButton.playIcon,
								pause: controlsConfig.playButton.pauseIcon,
								height: controlsConfig.playButton.height,
								width: controlsConfig.playButton.width,
							}}
						/>
					)}
					{controlsConfig?.muteButton && (
						<MuteButton
							setIsMuted={setIsMuted}
							isMuted={isMuted}
							icons={{
								muted: controlsConfig.muteButton.mutedIcon,
								unmuted: controlsConfig.muteButton.unmutedIcon,
								height: controlsConfig.muteButton.height,
								width: controlsConfig.muteButton.width,
							}}
						/>
					)}
				</div>
			)}
		</div>
	);
};

export default VimeoVideo;
